/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Stack,
  colors,
  Typography,
  Divider,
  FormControlLabel,
  Checkbox,
  Collapse,
  Skeleton,
} from '@mui/material';
import { ArrowBackIos } from '@mui/icons-material';
import { PaylinkResponse, currencyUtils } from 'skipify-types';
import { z } from 'zod';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import SkipifyButton from '../../../../components/core/Button/Button';
import { PaylinkSummary } from '../PaylinkSummary';
import { CustomerSummary } from '../CustomerSummary';
import { sanitizeNumericString } from '../../SendPaylinkForm/formValues';
import { formatCurrency } from '../../../../util/formatters';
import { Alert } from '../Alert';
import { RefundInput } from './RefundInput';
import { RefundableTransaction } from '../../../../store/modules/orders/types';
import { AcceptedCurrency } from '../../../../store/modules/merchant/types';

interface Props {
  paylink: PaylinkResponse;
  isLoadingSubmit?: boolean;
  isLoadingTransactions?: boolean;
  transactions: RefundableTransaction[];
  onClose: () => void;
  onSubmit: (
    refunds: { amount?: number; transactionId: string }[],
    isSplitPayment: boolean,
  ) => Promise<void>;
  currency?: AcceptedCurrency;
}

export const RefundPaylink = ({
  paylink,
  onClose,
  isLoadingSubmit,
  isLoadingTransactions,
  transactions,
  onSubmit,
  currency,
}: Props) => {
  const { customer } = paylink;
  const [selectedTransactions, setSelectedTransactions] = useState<string[]>([]);

  const validationSchema = z
    .object({
      transactions: z.array(
        z.object({
          skipifyTransactionId: z.string(),
          refundableAmount: z.number(),
          refundAmount: z.string().optional(),
        }),
      ),
    })
    .superRefine((data, ctx) => {
      data.transactions.forEach((transaction, index) => {
        // Skip validation if transaction is not selected
        if (!selectedTransactions.includes(transaction.skipifyTransactionId)) return;

        if (!transaction.refundAmount || transaction.refundAmount.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Refund Amount is required',
            path: ['transactions', index, 'refundAmount'],
          });
        } else {
          const amountNumber = Number(sanitizeNumericString(transaction.refundAmount));
          if (Number.isNaN(amountNumber) || amountNumber <= 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Invalid refund amount',
              path: ['transactions', index, 'refundAmount'],
            });
          } else if (amountNumber > transaction.refundableAmount) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Refund amount cannot be greater than available amount to refund',
              path: ['transactions', index, 'refundAmount'],
            });
          }
        }
      });
    });

  type FormValues = z.infer<typeof validationSchema>;

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitSuccessful },
    reset,
    watch,
  } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: { transactions: [] },
    reValidateMode: 'onBlur',
  });
  const { fields } = useFieldArray({
    control,
    name: 'transactions',
  });

  useEffect(() => {
    if (!isSubmitSuccessful) {
      return;
    }
    reset(undefined);
  }, [isSubmitSuccessful, reset]);

  useEffect(() => {
    reset({
      transactions: transactions.map(({ skipifyTransactionId, refundableAmount }) => {
        return {
          refundAmount: '',
          skipifyTransactionId,
          refundableAmount,
        };
      }),
    });
    if (transactions.length === 1) {
      setSelectedTransactions([transactions[0].skipifyTransactionId]);
    }
  }, [reset, transactions]);

  const isSplitPayment = transactions.length > 1;
  const totalRefundableAmount = transactions.reduce((acc, t) => acc + t.refundableAmount, 0);

  const isRefundDisabled =
    !isLoadingTransactions && (transactions.length === 0 || totalRefundableAmount === 0);

  const isSubmitDisabled = isRefundDisabled || selectedTransactions.length === 0;

  const inputValues = watch();

  const getTransactionText = (transaction: RefundableTransaction) => {
    let text = '';
    if (!transaction.cardBrand || !transaction.cardLastFour) {
      text = 'Unknown Payment Method ';
    } else {
      text = `${transaction.cardBrand} - ${transaction.cardLastFour} `;
    }

    text += `(${formatCurrency(currencyUtils.minorToMajor(transaction.amount ?? 0), paylink.currencyCode)})`;
    if (transaction.refundableAmount === 0) {
      text += ' - Refunded';
    }
    return text;
  };

  return (
    <Box
      sx={{
        backgroundColor: colors.common.white,
        width: '100%',
        height: '100%',
        pt: 3,
        px: 4,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        borderRadius: '8px',
        gap: 2,
      }}
    >
      <Stack>
        <Button
          sx={{
            '&&': {
              marginLeft: -1,
              alignSelf: 'flex-start',
            },
            '& > p': {
              fontSize: 12,
              fontWeight: 400,
              color: colors.grey[900],
              textTransform: 'none',
            },
            '& > svg': {
              fontSize: 14,
              color: colors.grey[900],
              marginRight: 0.5,
            },
          }}
          onClick={onClose}
        >
          <ArrowBackIos />
          <Typography>Back</Typography>
        </Button>
        <Typography component="h2" variant="xl" fontWeight="semibold" sx={{ mt: 2, mx: 0, mb: 2 }}>
          Refund Paylink
        </Typography>

        <Box
          component="form"
          id="refund-paylink"
          onSubmit={handleSubmit(async (data) => {
            try {
              await onSubmit(
                data.transactions
                  .filter((t) => selectedTransactions.includes(t.skipifyTransactionId))
                  .map((t) => ({
                    amount: Number(sanitizeNumericString(t.refundAmount)),
                    transactionId: t.skipifyTransactionId,
                  })),
                isSplitPayment,
              );
              // Close the modal only after successful refund
              onClose();
            } catch {
              // Don't close the modal if there was an error
              // The error handling is already done in useRefundPaylink
            }
          })}
        >
          <Stack gap={2}>
            <PaylinkSummary
              status={paylink.status}
              approvedAmount={paylink.approvedAmount}
              orderTotal={paylink.orderTotal ?? paylink.total}
              sendEmail={paylink.sendEmail}
              sendSms={paylink.sendSms}
              size="small"
              multiplePayments={isSplitPayment}
              enablePartialPayment={paylink.enablePartialPayment}
              currencyCode={paylink.currencyCode}
            />
            <CustomerSummary {...customer} />
            <Divider sx={{ borderColor: colors.grey[400] }} />
            {isLoadingTransactions && (
              <Stack>
                <Skeleton variant="rounded" height={60} sx={{ mb: 1 }} />
                <Skeleton variant="text" />
                <Skeleton variant="text" />
              </Stack>
            )}
            {!isLoadingTransactions && isSplitPayment && (
              <>
                <Typography
                  sx={{
                    fontSize: 14,
                  }}
                >
                  Choose the payment method you want to refund, then enter the refund amount to be
                  applied to it. You can refund only up to the maximum amount that was originally
                  paid using the payment method.
                </Typography>
                <Stack>
                  {isRefundDisabled && (
                    <Alert severity="warning" sx={{ py: 0 }}>
                      This Paylink has already been fully refunded.
                    </Alert>
                  )}
                  {fields.map((field, index) => {
                    const transaction = transactions.find(
                      (t) => t.skipifyTransactionId === field.skipifyTransactionId,
                    );

                    if (!transaction) return null;

                    return (
                      <Stack key={field.id}>
                        <FormControlLabel
                          sx={{
                            '& .MuiTypography-root': {
                              fontSize: 14,
                              color: colors.grey[800],
                              textTransform: 'capitalize',
                            },
                          }}
                          disabled={isRefundDisabled || transaction.refundableAmount === 0}
                          control={
                            <Checkbox
                              checked={selectedTransactions.includes(
                                transaction.skipifyTransactionId ?? '',
                              )}
                              onChange={(e) => {
                                setSelectedTransactions((prev) => {
                                  if (e.target.checked) {
                                    return [...prev, transaction.skipifyTransactionId ?? ''];
                                  }
                                  return prev.filter(
                                    (id) => id !== transaction.skipifyTransactionId,
                                  );
                                });
                              }}
                            />
                          }
                          label={getTransactionText(transaction)}
                        />
                        <Collapse
                          in={selectedTransactions.includes(transaction.skipifyTransactionId ?? '')}
                        >
                          <RefundInput
                            sx={{ py: 1 }}
                            name={`transactions.${index}.refundAmount`}
                            control={control}
                            disabled={isRefundDisabled}
                            errorMessage={errors.transactions?.[index]?.refundAmount?.message}
                            refundableAmount={field.refundableAmount}
                            value={inputValues.transactions[index]?.refundAmount ?? ''}
                            currency={currency}
                          />
                        </Collapse>
                      </Stack>
                    );
                  })}
                </Stack>
              </>
            )}
            {!isLoadingTransactions && !isSplitPayment && (
              <>
                {isRefundDisabled && (
                  <Alert severity="warning" sx={{ py: 0 }}>
                    {!transactions.length
                      ? 'This Paylink is not eligible to be refunded. Contact your admin.'
                      : 'This Paylink has already been fully refunded.'}
                  </Alert>
                )}
                <RefundInput
                  name="transactions.0.refundAmount"
                  control={control}
                  disabled={isRefundDisabled}
                  errorMessage={errors.transactions?.[0]?.refundAmount?.message}
                  refundableAmount={fields[0]?.refundableAmount ?? 0}
                  value={inputValues.transactions[0]?.refundAmount ?? ''}
                  currency={currency}
                />
              </>
            )}
          </Stack>
        </Box>
      </Stack>
      <Box>
        <Divider sx={{ mb: 2 }} />
        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          <SkipifyButton
            text="Cancel"
            onClick={onClose}
            theme="secondary"
            isFullWidth
            form="refund-paylink"
          />
          <SkipifyButton
            text="Send Refund"
            isFullWidth
            type="submit"
            form="refund-paylink"
            disabled={isLoadingSubmit || isSubmitDisabled}
            loading={isLoadingSubmit}
          />
        </Stack>
      </Box>
    </Box>
  );
};
